import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ArrowLeft, CheckCircle, Baby, Syringe, TrendingUp, Apple, 
  Smile, Heart, Activity, AlertCircle, Calendar, Globe
} from 'lucide-react';
import { toast } from 'sonner';

const ChildrensHealth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  
  // Child Profile State
  const [hasProfile, setHasProfile] = useState(false);
  const [childProfile, setChildProfile] = useState({
    name: '',
    dateOfBirth: '',
    gender: '',
    country: '',
    dietaryPreference: ''
  });
  
  // Active section navigation
  const [activeSection, setActiveSection] = useState(sectionParam || 'setup');
  const [view, setView] = useState('input'); // input or results
  
  // Immunization State
  const [immunizationData, setImmunizationData] = useState({
    completedVaccines: []
  });
  
  // Nutrition State
  const [nutritionData, setNutritionData] = useState({
    culture: 'African'
  });
  
  // Growth Tracking State
  const [growthData, setGrowthData] = useState({
    weight: '',
    height: '',
    history: []
  });
  
  // Mental Health State
  const [mentalData, setMentalData] = useState({
    mood: '',
    behavior: '',
    sleepQuality: '',
    appetiteLevel: '',
    energyLevel: '',
    socialInteraction: '',
    screenTime: '',
    notes: ''
  });
  
  // Daily Health Log State
  const [dailyLogData, setDailyLogData] = useState({
    meals: '',
    sleep: '',
    activity: '',
    symptoms: ''
  });
  
  const [results, setResults] = useState(null);
  const [alerts, setAlerts] = useState([]);

  // Calculate age from DOB
  const calculateAge = (dob) => {
    if (!dob) return { years: 0, months: 0 };
    const birthDate = new Date(dob);
    const today = new Date();
    const ageInMonths = (today.getFullYear() - birthDate.getFullYear()) * 12 + (today.getMonth() - birthDate.getMonth());
    return {
      years: Math.floor(ageInMonths / 12),
      months: ageInMonths % 12,
      totalMonths: ageInMonths
    };
  };

  // Get age group for nutrition
  const getAgeGroup = (totalMonths) => {
    if (totalMonths < 6) return '0-6 months';
    if (totalMonths < 12) return '6-12 months';
    if (totalMonths < 36) return '1-3 years';
    return '4-12 years';
  };

  // Handle Profile Setup
  const handleProfileSetup = (e) => {
    e.preventDefault();
    if (!childProfile.name || !childProfile.dateOfBirth || !childProfile.gender || !childProfile.country) {
      toast.error('Please complete all required fields');
      return;
    }

    setHasProfile(true);
    setActiveSection('dashboard');
    toast.success(`Profile created for ${childProfile.name}! 🎉`);
    
    // Check for alerts
    checkAlerts();
  };

  // Check and generate alerts
  const checkAlerts = () => {
    const newAlerts = [];
    const age = calculateAge(childProfile.dateOfBirth);
    
    // Check immunization alerts
    if (age.totalMonths >= 2 && immunizationData.completedVaccines.length === 0) {
      newAlerts.push({
        type: 'warning',
        title: 'Immunization Due',
        message: 'Your child may be due for vaccinations. Check the immunization tracker.'
      });
    }
    
    // Check growth tracking
    if (growthData.history.length === 0 && age.totalMonths >= 1) {
      newAlerts.push({
        type: 'info',
        title: 'Track Growth',
        message: 'Start tracking your child\'s growth to monitor development.'
      });
    }
    
    setAlerts(newAlerts);
  };

  // Handle Immunization Tracker
  const handleImmunizationSubmit = (e) => {
    e.preventDefault();
    const age = calculateAge(childProfile.dateOfBirth);
    const schedule = getVaccineSchedule(age.totalMonths, childProfile.country);
    
    setResults({ schedule });
    setView('results');
    toast.success('Vaccine schedule loaded!');
  };

  const getVaccineSchedule = (ageMonths, country) => {
    const vaccines = [];
    
    // Birth - 2 months
    if (ageMonths >= 0) {
      vaccines.push({
        name: 'Hepatitis B',
        age: 'Birth - 2 months',
        completed: immunizationData.completedVaccines.includes('hepb'),
        priority: 'High'
      });
    }
    
    // 2 months
    if (ageMonths >= 2) {
      vaccines.push(
        {
          name: 'DTaP (Diphtheria, Tetanus, Pertussis)',
          age: '2 months',
          completed: immunizationData.completedVaccines.includes('dtap1'),
          priority: 'High'
        },
        {
          name: 'Polio (IPV)',
          age: '2 months',
          completed: immunizationData.completedVaccines.includes('polio1'),
          priority: 'High'
        },
        {
          name: 'Hib (Haemophilus influenzae)',
          age: '2 months',
          completed: immunizationData.completedVaccines.includes('hib1'),
          priority: 'High'
        },
        {
          name: 'Pneumococcal (PCV)',
          age: '2 months',
          completed: immunizationData.completedVaccines.includes('pcv1'),
          priority: 'High'
        }
      );
    }
    
    // 6 months
    if (ageMonths >= 6) {
      vaccines.push({
        name: 'Influenza (Yearly)',
        age: '6 months+',
        completed: immunizationData.completedVaccines.includes('flu'),
        priority: 'Medium'
      });
    }
    
    // 12 months
    if (ageMonths >= 12) {
      vaccines.push(
        {
          name: 'MMR (Measles, Mumps, Rubella)',
          age: '12 months',
          completed: immunizationData.completedVaccines.includes('mmr1'),
          priority: 'High'
        },
        {
          name: 'Varicella (Chickenpox)',
          age: '12 months',
          completed: immunizationData.completedVaccines.includes('varicella'),
          priority: 'High'
        }
      );
    }
    
    return vaccines;
  };

  const markVaccineComplete = (vaccineName) => {
    const vaccineCode = vaccineName.toLowerCase().replace(/\s+/g, '');
    setImmunizationData(prev => ({
      ...prev,
      completedVaccines: [...prev.completedVaccines, vaccineCode]
    }));
    toast.success(`${vaccineName} marked as completed! 💉`);
    
    // Refresh schedule
    const age = calculateAge(childProfile.dateOfBirth);
    const schedule = getVaccineSchedule(age.totalMonths, childProfile.country);
    setResults({ schedule });
  };

  // Handle Nutrition
  const handleNutritionSubmit = (e) => {
    e.preventDefault();
    const age = calculateAge(childProfile.dateOfBirth);
    const ageGroup = getAgeGroup(age.totalMonths);
    const mealPlan = getNutritionPlan(ageGroup, nutritionData.culture);
    
    setResults({ ageGroup, mealPlan });
    setView('results');
    toast.success('Meal plan ready! 🍎');
  };

  const getNutritionPlan = (ageGroup, culture) => {
    const plans = {
      '0-6 months': {
        African: {
          foods: ['Breast milk (exclusive)', 'Formula (if needed)'],
          avoid: ['Solid foods', 'Water', 'Juice'],
          tips: ['Breastfeed on demand', 'Ensure proper latch', 'Stay hydrated']
        },
        'UK/Western': {
          foods: ['Breast milk (exclusive)', 'Formula (if needed)'],
          avoid: ['Solid foods', 'Water', 'Honey'],
          tips: ['Feed every 2-3 hours', 'Burp after feeding', 'Track wet diapers']
        },
        Asian: {
          foods: ['Breast milk (exclusive)', 'Formula (if needed)'],
          avoid: ['Solid foods', 'Tea', 'Herbal remedies'],
          tips: ['Maintain feeding schedule', 'Monitor growth', 'Consult pediatrician']
        }
      },
      '6-12 months': {
        African: {
          foods: ['Breast milk', 'Mashed yam', 'Pureed vegetables', 'Soft plantain', 'Ground nut soup (no whole nuts)', 'Mashed beans'],
          avoid: ['Honey', 'Whole nuts', 'Raw vegetables', 'Cow milk'],
          tips: ['Introduce one food at a time', 'Watch for allergies', 'Offer iron-rich foods'],
          meals: ['Breakfast: Pap (fermented cereal)', 'Lunch: Mashed yam with vegetable puree', 'Dinner: Soft rice with mashed beans']
        },
        'UK/Western': {
          foods: ['Breast milk', 'Baby rice cereal', 'Pureed fruits', 'Mashed vegetables', 'Soft pasta', 'Yogurt'],
          avoid: ['Honey', 'Salt', 'Sugar', 'Whole nuts'],
          tips: ['Start with single-ingredient purees', 'Gradually increase texture', 'Offer variety'],
          meals: ['Breakfast: Baby cereal with mashed banana', 'Lunch: Mashed sweet potato', 'Dinner: Pureed chicken with vegetables']
        },
        Asian: {
          foods: ['Breast milk', 'Rice porridge (congee)', 'Mashed tofu', 'Steamed vegetables', 'Soft noodles', 'Pureed fish'],
          avoid: ['Honey', 'Soy sauce', 'Raw fish', 'Whole grains'],
          tips: ['Steam foods for easy digestion', 'Use mild flavors', 'Ensure soft texture'],
          meals: ['Breakfast: Rice congee', 'Lunch: Steamed egg with vegetables', 'Dinner: Soft noodles with mashed fish']
        }
      },
      '1-3 years': {
        African: {
          foods: ['Jollof rice', 'Plantain', 'Beans', 'Vegetables', 'Fruits', 'Fish', 'Chicken', 'Yam', 'Eggs'],
          avoid: ['Spicy foods', 'Whole nuts (choking hazard)', 'Excess sugar'],
          tips: ['3 meals + 2 snacks', 'Encourage self-feeding', 'Limit juice'],
          meals: ['Breakfast: Moi moi (bean pudding) with bread', 'Lunch: Jollof rice with chicken and vegetables', 'Dinner: Pounded yam with vegetable soup', 'Snacks: Sliced mango, plantain chips']
        },
        'UK/Western': {
          foods: ['Whole grain bread', 'Pasta', 'Cheese', 'Vegetables', 'Fruits', 'Chicken', 'Fish', 'Eggs', 'Milk'],
          avoid: ['Choking hazards', 'Excess salt', 'Sugary drinks'],
          tips: ['Offer balanced meals', 'Let child self-feed', 'Make mealtimes positive'],
          meals: ['Breakfast: Scrambled eggs with toast', 'Lunch: Pasta with tomato sauce and cheese', 'Dinner: Chicken with mashed potatoes and peas', 'Snacks: Apple slices, cheese cubes']
        },
        Asian: {
          foods: ['Rice', 'Noodles', 'Tofu', 'Vegetables', 'Fish', 'Chicken', 'Eggs', 'Fruits', 'Soups'],
          avoid: ['Raw fish', 'Spicy sauces', 'Whole nuts'],
          tips: ['Serve family-style meals', 'Use chopsticks for motor skills', 'Include soups'],
          meals: ['Breakfast: Rice porridge with egg', 'Lunch: Stir-fried vegetables with tofu and rice', 'Dinner: Chicken soup with soft noodles', 'Snacks: Steamed buns, sliced oranges']
        }
      },
      '4-12 years': {
        African: {
          foods: ['Whole grains', 'Proteins (beans, fish, chicken)', 'Vegetables', 'Fruits', 'Dairy', 'Healthy fats'],
          avoid: ['Excessive fried foods', 'Sugary drinks', 'Processed snacks'],
          tips: ['Encourage water intake', 'Involve child in meal prep', 'Balance plate with colors'],
          meals: ['Breakfast: Akara (bean cakes) with pap', 'Lunch: Jollof rice with grilled fish and salad', 'Dinner: Egusi soup with fufu and vegetables', 'Snacks: Tiger nuts, fresh fruits']
        },
        'UK/Western': {
          foods: ['Whole grains', 'Lean proteins', 'Vegetables', 'Fruits', 'Dairy', 'Healthy fats'],
          avoid: ['Fast food', 'Sugary cereals', 'Soda'],
          tips: ['Limit screen time during meals', 'Teach portion control', 'Encourage physical activity'],
          meals: ['Breakfast: Oatmeal with berries', 'Lunch: Whole grain sandwich with turkey and veggies', 'Dinner: Baked salmon with quinoa and broccoli', 'Snacks: Yogurt, carrot sticks with hummus']
        },
        Asian: {
          foods: ['Rice/noodles', 'Proteins (tofu, fish, chicken)', 'Vegetables', 'Fruits', 'Soups', 'Green tea'],
          avoid: ['Excess sodium', 'Fried foods', 'Sugary drinks'],
          tips: ['Practice mindful eating', 'Use variety of vegetables', 'Maintain meal routines'],
          meals: ['Breakfast: Congee with century egg and vegetables', 'Lunch: Fried rice with chicken and vegetables', 'Dinner: Hot pot with assorted vegetables and proteins', 'Snacks: Edamame, fruit slices']
        }
      }
    };
    
    return plans[ageGroup][culture] || plans[ageGroup]['African'];
  };

  // Handle Growth Tracking
  const handleGrowthSubmit = (e) => {
    e.preventDefault();
    if (!growthData.weight || !growthData.height) {
      toast.error('Please enter both weight and height');
      return;
    }

    const age = calculateAge(childProfile.dateOfBirth);
    const assessment = assessGrowth(parseFloat(growthData.weight), parseFloat(growthData.height), age.totalMonths);
    
    // Add to history
    const newEntry = {
      date: new Date().toISOString().split('T')[0],
      weight: parseFloat(growthData.weight),
      height: parseFloat(growthData.height),
      ageMonths: age.totalMonths
    };
    
    setGrowthData(prev => ({
      ...prev,
      history: [newEntry, ...prev.history]
    }));
    
    setResults({ assessment, newEntry });
    setView('results');
    toast.success('Growth data recorded! 📊');
  };

  const assessGrowth = (weight, height, ageMonths) => {
    // Simplified growth assessment (real app would use WHO growth charts)
    let status = 'Normal';
    let recommendation = 'Growth is within normal range. Continue monitoring.';
    
    // Basic ranges (simplified - real app needs proper percentiles)
    if (ageMonths <= 6) {
      if (weight < 3.5 || weight > 9) status = 'Needs Attention';
      if (height < 50 || height > 70) status = 'Needs Attention';
    } else if (ageMonths <= 12) {
      if (weight < 6 || weight > 12) status = 'Needs Attention';
      if (height < 65 || height > 80) status = 'Needs Attention';
    } else if (ageMonths <= 36) {
      if (weight < 8 || weight > 18) status = 'Needs Attention';
      if (height < 75 || height > 105) status = 'Needs Attention';
    }
    
    if (status === 'Needs Attention') {
      recommendation = 'Growth is outside typical range. Please consult your pediatrician for proper assessment.';
    }
    
    return { status, recommendation, weight, height };
  };

  // Handle Mental Health
  const handleMentalSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!mentalData.mood || !mentalData.behavior || !mentalData.sleepQuality || 
        !mentalData.appetiteLevel || !mentalData.energyLevel || 
        !mentalData.socialInteraction || !mentalData.screenTime) {
      toast.error('Please complete all required fields');
      return;
    }

    const analysis = analyzeMentalWellness(mentalData);
    
    setResults(analysis);
    setView('results');
    toast.success('Insights ready! 😊');
  };

  const analyzeMentalWellness = (data) => {
    const tips = [];
    const positiveSignals = [];
    const concernAreas = [];
    let overallWellbeing = 'Good';
    let concernScore = 0;

    // Mood Analysis
    if (data.mood === 'Happy' || data.mood === 'Calm') {
      positiveSignals.push('✅ Positive mood today');
    } else if (data.mood === 'Sad') {
      concernScore += 2;
      concernAreas.push('⚠️ Low mood detected');
      tips.push({
        icon: '💙',
        area: 'Mood Support',
        advice: 'Your child is experiencing sadness',
        actions: [
          'Spend extra quality one-on-one time together',
          'Engage in favorite activities or comfort rituals',
          'Validate their feelings: "It\'s okay to feel sad"',
          'If sadness persists for 2+ weeks, consult a pediatrician'
        ]
      });
    } else if (data.mood === 'Fussy') {
      concernScore += 1;
      concernAreas.push('⚠️ Irritability noted');
      tips.push({
        icon: '🤗',
        area: 'Emotional Regulation',
        advice: 'Child showing signs of irritability',
        actions: [
          'Check for unmet needs (hunger, tiredness, overstimulation)',
          'Offer calming activities like reading or quiet play',
          'Teach simple breathing exercises',
          'Maintain predictable routines'
        ]
      });
    }

    // Behavior Analysis
    if (data.behavior === 'Cooperative' || data.behavior === 'Playful') {
      positiveSignals.push('✅ Positive behavior patterns');
    } else if (data.behavior === 'Aggressive' || data.behavior === 'Defiant') {
      concernScore += 3;
      concernAreas.push('🚨 Challenging behavior');
      tips.push({
        icon: '🛡️',
        area: 'Behavior Management',
        advice: 'Aggressive or defiant behavior detected',
        actions: [
          'Use calm, firm language and clear boundaries',
          'Implement consistent consequences',
          'Teach conflict resolution skills',
          'Reward positive behavior immediately',
          'Consider consulting a child psychologist if persistent'
        ]
      });
    } else if (data.behavior === 'Withdrawn') {
      concernScore += 2;
      concernAreas.push('⚠️ Social withdrawal');
      tips.push({
        icon: '🤝',
        area: 'Social Development',
        advice: 'Child is withdrawn or less engaged',
        actions: [
          'Arrange low-pressure playdates with familiar peers',
          'Join parent-child group activities',
          'Practice social skills through role-play',
          'Monitor for signs of bullying or anxiety'
        ]
      });
    } else if (data.behavior === 'Anxious') {
      concernScore += 2;
      concernAreas.push('⚠️ Anxiety symptoms');
      tips.push({
        icon: '🌈',
        area: 'Anxiety Management',
        advice: 'Child showing signs of anxiety',
        actions: [
          'Create a safe, predictable environment',
          'Teach simple coping strategies (deep breaths, counting)',
          'Gradually expose to anxiety triggers in a supportive way',
          'Seek professional help if anxiety interferes with daily life'
        ]
      });
    }

    // Sleep Quality Analysis
    if (data.sleepQuality === 'Poor') {
      concernScore += 2;
      concernAreas.push('⚠️ Sleep difficulties');
      tips.push({
        icon: '😴',
        area: 'Sleep Health',
        advice: 'Poor sleep quality detected',
        actions: [
          'Establish consistent bedtime routine (bath, story, cuddles)',
          'Limit screen time 1-2 hours before bed',
          'Ensure bedroom is dark, quiet, and cool',
          'Avoid sugary foods/drinks in the evening'
        ]
      });
    } else if (data.sleepQuality === 'Good') {
      positiveSignals.push('✅ Healthy sleep patterns');
    }

    // Appetite Analysis
    if (data.appetiteLevel === 'Poor') {
      concernScore += 1;
      concernAreas.push('⚠️ Low appetite');
      tips.push({
        icon: '🍎',
        area: 'Nutrition & Appetite',
        advice: 'Low appetite observed',
        actions: [
          'Offer small, frequent meals and snacks',
          'Make mealtimes fun and pressure-free',
          'Involve child in meal preparation',
          'Rule out illness or stress-related causes'
        ]
      });
    } else if (data.appetiteLevel === 'Good' || data.appetiteLevel === 'Excellent') {
      positiveSignals.push('✅ Healthy appetite');
    }

    // Energy Level Analysis
    if (data.energyLevel === 'Low') {
      concernScore += 1;
      concernAreas.push('⚠️ Low energy');
      tips.push({
        icon: '⚡',
        area: 'Energy & Activity',
        advice: 'Low energy levels noted',
        actions: [
          'Ensure adequate sleep (age-appropriate hours)',
          'Provide nutritious, balanced meals',
          'Check for signs of illness or anemia',
          'Encourage gentle outdoor play'
        ]
      });
    } else if (data.energyLevel === 'High' || data.energyLevel === 'Moderate') {
      positiveSignals.push('✅ Good energy levels');
    }

    // Social Interaction Analysis
    if (data.socialInteraction === 'Avoids Others') {
      concernScore += 2;
      concernAreas.push('⚠️ Social avoidance');
      tips.push({
        icon: '👫',
        area: 'Social Skills',
        advice: 'Avoiding social interaction',
        actions: [
          'Start with one-on-one playdates with trusted friends',
          'Model positive social behavior',
          'Praise small social successes',
          'Investigate potential bullying or shyness'
        ]
      });
    } else if (data.socialInteraction === 'Plays Normally' || data.socialInteraction === 'Very Social') {
      positiveSignals.push('✅ Healthy social engagement');
    }

    // Screen Time Analysis
    if (data.screenTime === 'Excessive (3+ hours)') {
      concernScore += 1;
      tips.push({
        icon: '📱',
        area: 'Screen Time Management',
        advice: 'High screen time detected',
        actions: [
          'Set age-appropriate limits (AAP recommends max 1-2 hours/day for kids)',
          'Create screen-free zones (bedrooms, dinner table)',
          'Replace screen time with physical activities',
          'Use parental controls and monitor content'
        ]
      });
    } else if (data.screenTime === 'Minimal (< 1 hour)' || data.screenTime === 'Moderate (1-2 hours)') {
      positiveSignals.push('✅ Healthy screen time habits');
    }

    // Overall Wellbeing Assessment
    if (concernScore >= 5) {
      overallWellbeing = 'Needs Attention';
    } else if (concernScore >= 2) {
      overallWellbeing = 'Fair';
    } else {
      overallWellbeing = 'Good';
    }

    // Always include general wellness tips
    tips.push({
      icon: '🌟',
      area: 'General Wellbeing',
      advice: 'Supporting overall mental health',
      actions: [
        'Maintain consistent daily routines',
        'Encourage 60 minutes of physical activity daily',
        'Limit processed foods and sugar',
        'Practice mindfulness or calming activities together',
        'Model healthy emotional expression'
      ]
    });

    // Add professional help recommendation if needed
    if (concernScore >= 4) {
      tips.push({
        icon: '👨‍⚕️',
        area: 'Professional Support',
        advice: 'Consider seeking expert guidance',
        actions: [
          'Consult your pediatrician about behavioral concerns',
          'Ask for referral to child psychologist if needed',
          'School counselors can provide additional support',
          'Early intervention leads to better outcomes'
        ]
      });
    }

    return {
      overallWellbeing,
      concernScore,
      positiveSignals,
      concernAreas,
      tips
    };
  };

  // Handle Daily Health Log
  const handleDailyLogSubmit = (e) => {
    e.preventDefault();
    if (!dailyLogData.meals || !dailyLogData.sleep || !dailyLogData.activity) {
      toast.error('Please complete all fields');
      return;
    }

    const feedback = analyzeDailyLog(dailyLogData);
    
    setResults({ feedback });
    setView('results');
    toast.success('Daily log recorded! 📝');
  };

  const analyzeDailyLog = (log) => {
    const feedback = [];
    
    // Analyze meals
    if (log.meals === 'Varied') {
      feedback.push({ type: 'positive', message: 'Excellent nutrition variety today! 🌟' });
    } else if (log.meals === 'Limited') {
      feedback.push({ type: 'warning', message: 'Try to offer more variety in foods.' });
    } else if (log.meals === 'Poor') {
      feedback.push({ type: 'alert', message: 'Low nutrition intake detected. Consider consulting a pediatrician.' });
    }
    
    // Analyze sleep
    if (log.sleep === 'Good') {
      feedback.push({ type: 'positive', message: 'Great sleep quality! Keep up the bedtime routine. 😴' });
    } else if (log.sleep === 'Fair') {
      feedback.push({ type: 'info', message: 'Sleep could be improved. Try earlier bedtime.' });
    } else {
      feedback.push({ type: 'warning', message: 'Poor sleep quality. Check sleep environment and routine.' });
    }
    
    // Analyze activity
    if (log.activity === 'Very Active') {
      feedback.push({ type: 'positive', message: 'Excellent physical activity! 🏃' });
    } else if (log.activity === 'Moderate') {
      feedback.push({ type: 'positive', message: 'Good activity level maintained.' });
    } else {
      feedback.push({ type: 'info', message: 'Try to increase active play time for development.' });
    }
    
    // Check for symptoms
    if (log.symptoms && log.symptoms !== 'None') {
      feedback.push({ type: 'alert', message: `Symptoms noted: ${log.symptoms}. Monitor and consult doctor if persistent.` });
    }
    
    return feedback;
  };

  const resetSection = () => {
    setView('input');
    setResults(null);
  };

  // Profile Setup View
  if (!hasProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50 pb-20">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Children's Health v2</h1>
            <p className="text-gray-600">Create your child's profile to get started</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Baby className="h-5 w-5 text-blue-600" />
                Child Profile Setup
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileSetup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Child's Name *
                  </label>
                  <Input
                    type="text"
                    value={childProfile.name}
                    onChange={(e) => setChildProfile({ ...childProfile, name: e.target.value })}
                    placeholder="Enter child's name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth *
                  </label>
                  <Input
                    type="date"
                    value={childProfile.dateOfBirth}
                    onChange={(e) => setChildProfile({ ...childProfile, dateOfBirth: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {['Male', 'Female', 'Other'].map(gender => (
                      <button
                        key={gender}
                        type="button"
                        onClick={() => setChildProfile({ ...childProfile, gender })}
                        className={`p-3 rounded-lg border-2 font-medium transition-all ${
                          childProfile.gender === gender
                            ? 'border-blue-600 bg-blue-100 text-blue-900'
                            : 'border-gray-300 hover:border-blue-300'
                        }`}
                      >
                        {gender}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country/Region *
                  </label>
                  <select
                    value={childProfile.country}
                    onChange={(e) => setChildProfile({ ...childProfile, country: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select country</option>
                    <option value="Nigeria">Nigeria</option>
                    <option value="Ghana">Ghana</option>
                    <option value="Kenya">Kenya</option>
                    <option value="South Africa">South Africa</option>
                    <option value="UK">United Kingdom</option>
                    <option value="USA">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="India">India</option>
                    <option value="China">China</option>
                    <option value="Japan">Japan</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dietary Preference (Optional)
                  </label>
                  <select
                    value={childProfile.dietaryPreference}
                    onChange={(e) => setChildProfile({ ...childProfile, dietaryPreference: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">None</option>
                    <option value="Vegetarian">Vegetarian</option>
                    <option value="Vegan">Vegan</option>
                    <option value="Halal">Halal</option>
                    <option value="Kosher">Kosher</option>
                    <option value="Gluten-Free">Gluten-Free</option>
                    <option value="Dairy-Free">Dairy-Free</option>
                  </select>
                </div>

                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg font-bold">
                  Create Profile & Continue
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const age = calculateAge(childProfile.dateOfBirth);

  // Dashboard View
  if (activeSection === 'dashboard') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50 pb-20">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              {childProfile.name} is {age.years} {age.years === 1 ? 'year' : 'years'} {age.months} {age.months === 1 ? 'month' : 'months'}
            </h1>
            <p className="text-gray-600">{childProfile.gender} • {childProfile.country}</p>
          </div>

          {/* Alerts */}
          {alerts.length > 0 && (
            <div className="mb-6 space-y-3">
              {alerts.map((alert, idx) => (
                <Card key={idx} className={`border-2 ${
                  alert.type === 'warning' ? 'border-amber-300 bg-amber-50' : 'border-blue-300 bg-blue-50'
                }`}>
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className={`h-5 w-5 ${
                        alert.type === 'warning' ? 'text-amber-600' : 'text-blue-600'
                      }`} />
                      <div>
                        <h4 className="font-bold text-gray-900">{alert.title}</h4>
                        <p className="text-sm text-gray-700">{alert.message}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6 text-center">
                <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Growth Entries</p>
                <p className="text-2xl font-bold text-gray-900">{growthData.history.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <Syringe className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Vaccines Done</p>
                <p className="text-2xl font-bold text-gray-900">{immunizationData.completedVaccines.length}</p>
              </CardContent>
            </Card>
          </div>

          {/* Module Navigation */}
          <h2 className="text-lg font-bold text-gray-900 mb-4">Health Modules</h2>
          <div className="grid grid-cols-1 gap-4">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {setActiveSection('immunization'); setView('input');}}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <Syringe className="h-10 w-10 text-blue-600" />
                  <div>
                    <h3 className="font-bold text-gray-900">Immunization Tracker</h3>
                    <p className="text-sm text-gray-600">View vaccine schedule and track completion</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {setActiveSection('nutrition'); setView('input');}}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <Apple className="h-10 w-10 text-green-600" />
                  <div>
                    <h3 className="font-bold text-gray-900">Nutrition by Age</h3>
                    <p className="text-sm text-gray-600">Age-appropriate meal plans and feeding tips</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {setActiveSection('growth'); setView('input');}}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <TrendingUp className="h-10 w-10 text-purple-600" />
                  <div>
                    <h3 className="font-bold text-gray-900">Growth Tracker</h3>
                    <p className="text-sm text-gray-600">Monitor weight and height development</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {setActiveSection('mental'); setView('input');}}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <Smile className="h-10 w-10 text-yellow-600" />
                  <div>
                    <h3 className="font-bold text-gray-900">Mental & Behavioral Health</h3>
                    <p className="text-sm text-gray-600">Track mood and get parenting tips</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {setActiveSection('daily'); setView('input');}}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <Activity className="h-10 w-10 text-orange-600" />
                  <div>
                    <h3 className="font-bold text-gray-900">Daily Health Log</h3>
                    <p className="text-sm text-gray-600">Log meals, sleep, and daily activities</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Results View
  if (view === 'results' && results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50 pb-20">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <Button variant="ghost" onClick={() => setActiveSection('dashboard')} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Overview
          </Button>

          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            {activeSection === 'immunization' && 'Vaccine Schedule'}
            {activeSection === 'nutrition' && 'Nutrition Plan'}
            {activeSection === 'growth' && 'Growth Assessment'}
            {activeSection === 'mental' && 'Mental Health Insights'}
            {activeSection === 'daily' && 'Daily Health Feedback'}
          </h1>

          <Card className="mb-6 bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-10 w-10 text-green-600" />
                <div>
                  <h3 className="font-bold text-gray-900">Analysis Complete!</h3>
                  <p className="text-sm text-gray-600">Here are your personalized recommendations</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Immunization Results */}
          {activeSection === 'immunization' && results.schedule && (
            <div className="space-y-4 mb-6">
              <h3 className="font-bold text-gray-900">Vaccine Schedule for {childProfile.name}</h3>
              {results.schedule.map((vaccine, idx) => (
                <Card key={idx} className={`border-2 ${
                  vaccine.completed ? 'border-green-200 bg-green-50' : 
                  vaccine.priority === 'High' ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'
                }`}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900">{vaccine.name}</h4>
                        <p className="text-sm text-gray-600">Due age: {vaccine.age}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded ${
                          vaccine.priority === 'High' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {vaccine.priority} Priority
                        </span>
                        {vaccine.completed ? (
                          <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800">✓ Done</span>
                        ) : (
                          <Button 
                            size="sm" 
                            onClick={() => markVaccineComplete(vaccine.name)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Mark Done
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Nutrition Results */}
          {activeSection === 'nutrition' && results.mealPlan && (
            <div className="space-y-4 mb-6">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-4">
                  <h4 className="font-bold text-gray-900 mb-2">Age Group: {results.ageGroup}</h4>
                  <p className="text-sm text-gray-700">Culture: {nutritionData.culture}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recommended Foods</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {results.mealPlan.foods.map((food, idx) => (
                      <span key={idx} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                        {food}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {results.mealPlan.meals && (
                <Card>
                  <CardHeader>
                    <CardTitle>Sample Meal Plan</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {results.mealPlan.meals.map((meal, idx) => (
                        <p key={idx} className="text-sm text-gray-700">• {meal}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="border-2 border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-base">Foods to Avoid</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {results.mealPlan.avoid.map((food, idx) => (
                      <p key={idx} className="text-sm text-gray-700">• {food}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Feeding Tips</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {results.mealPlan.tips.map((tip, idx) => (
                      <p key={idx} className="text-sm text-gray-700">• {tip}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Growth Results */}
          {activeSection === 'growth' && results.assessment && (
            <div className="space-y-4 mb-6">
              <Card className={`border-2 ${
                results.assessment.status === 'Normal' ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'
              }`}>
                <CardContent className="pt-6">
                  <h4 className="font-bold text-gray-900 mb-2">Growth Status: {results.assessment.status}</h4>
                  <p className="text-sm text-gray-700">{results.assessment.recommendation}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Latest Measurement</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-600">Weight</p>
                      <p className="text-2xl font-bold text-gray-900">{results.newEntry.weight} kg</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <p className="text-sm text-gray-600">Height</p>
                      <p className="text-2xl font-bold text-gray-900">{results.newEntry.height} cm</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {growthData.history.length > 1 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Growth History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {growthData.history.slice(0, 5).map((entry, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span className="text-sm text-gray-600">{entry.date}</span>
                          <span className="text-sm text-gray-900">{entry.weight} kg • {entry.height} cm</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Mental Health Results */}
          {activeSection === 'mental' && results.tips && (
            <>
              {/* Overall Wellbeing Card */}
              <Card className={`mb-6 ${
                results.overallWellbeing === 'Good' ? 'bg-green-50 border-green-300' :
                results.overallWellbeing === 'Fair' ? 'bg-amber-50 border-amber-300' :
                'bg-red-50 border-red-300'
              }`}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Smile className={`h-10 w-10 ${
                      results.overallWellbeing === 'Good' ? 'text-green-600' :
                      results.overallWellbeing === 'Fair' ? 'text-amber-600' :
                      'text-red-600'
                    }`} />
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">
                        Overall Wellbeing: {results.overallWellbeing}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {results.overallWellbeing === 'Good' && 'Your child is showing positive mental wellness signs'}
                        {results.overallWellbeing === 'Fair' && 'Some areas could benefit from attention and support'}
                        {results.overallWellbeing === 'Needs Attention' && 'Several concerns detected - consider professional support'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Positive Signals */}
              {results.positiveSignals && results.positiveSignals.length > 0 && (
                <Card className="mb-6 bg-green-50 border-green-200">
                  <CardHeader>
                    <CardTitle className="text-base text-green-800 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Positive Signs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {results.positiveSignals.map((signal, idx) => (
                        <p key={idx} className="text-sm text-gray-700 font-medium">{signal}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Concern Areas */}
              {results.concernAreas && results.concernAreas.length > 0 && (
                <Card className="mb-6 bg-amber-50 border-amber-200">
                  <CardHeader>
                    <CardTitle className="text-base text-amber-800 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5" />
                      Areas to Monitor
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {results.concernAreas.map((area, idx) => (
                        <p key={idx} className="text-sm text-gray-700 font-medium">{area}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Personalized Tips */}
              <h3 className="text-lg font-bold text-gray-900 mb-4">Personalized Guidance</h3>
              <div className="space-y-4 mb-6">
                {results.tips.map((tip, idx) => (
                  <Card key={idx}>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <span className="text-2xl">{tip.icon}</span>
                        {tip.area}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-700 mb-3"><strong>Insight:</strong> {tip.advice}</p>
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-gray-900">Recommended Actions:</p>
                        {tip.actions.map((action, aidx) => (
                          <p key={aidx} className="text-sm text-gray-700">• {action}</p>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}

          {/* Daily Log Results */}
          {activeSection === 'daily' && results.feedback && (
            <div className="space-y-4 mb-6">
              {results.feedback.map((item, idx) => (
                <Card key={idx} className={`border-2 ${
                  item.type === 'positive' ? 'border-green-200 bg-green-50' :
                  item.type === 'warning' ? 'border-amber-200 bg-amber-50' :
                  item.type === 'alert' ? 'border-red-200 bg-red-50' :
                  'border-blue-200 bg-blue-50'
                }`}>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-900">{item.message}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <Card>
            <CardContent className="pt-6 space-y-3">
              <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={resetSection}>
                {activeSection === 'immunization' && 'View Schedule Again'}
                {activeSection === 'nutrition' && 'Switch Culture'}
                {activeSection === 'growth' && 'Track New Measurement'}
                {activeSection === 'mental' && 'Log New Entry'}
                {activeSection === 'daily' && 'Log Another Day'}
              </Button>
              <Button className="w-full" variant="outline" onClick={() => setActiveSection('dashboard')}>
                Back to Overview
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Input Forms View (renders after component continues...)
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50 pb-20">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Button variant="ghost" onClick={() => setActiveSection('dashboard')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Overview
        </Button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {activeSection === 'immunization' && 'Immunization Tracker'}
            {activeSection === 'nutrition' && 'Nutrition by Age'}
            {activeSection === 'growth' && 'Growth Tracker'}
            {activeSection === 'mental' && 'Mental & Behavioral Health'}
            {activeSection === 'daily' && 'Daily Health Log'}
          </h1>
          <p className="text-gray-600">
            {activeSection === 'immunization' && `Track ${childProfile.name}'s vaccinations`}
            {activeSection === 'nutrition' && `Age-appropriate nutrition for ${childProfile.name}`}
            {activeSection === 'growth' && `Monitor ${childProfile.name}'s growth`}
            {activeSection === 'mental' && `Track ${childProfile.name}'s mood and behavior`}
            {activeSection === 'daily' && `Log ${childProfile.name}'s daily health`}
          </p>
        </div>

        {/* Immunization Form */}
        {activeSection === 'immunization' && (
          <form onSubmit={handleImmunizationSubmit} className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle>Vaccine Schedule Information</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700 mb-4">
                  Based on {childProfile.name}'s age ({age.years} years {age.months} months), we'll show the recommended vaccine schedule.
                </p>
                <p className="text-sm text-gray-600">
                  Country: {childProfile.country}
                </p>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg font-bold">
              View Full Schedule 💉
            </Button>
          </form>
        )}

        {/* Nutrition Form */}
        {activeSection === 'nutrition' && (
          <form onSubmit={handleNutritionSubmit} className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle>Age Group</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-bold text-gray-900">{getAgeGroup(age.totalMonths)}</p>
                <p className="text-sm text-gray-600">Auto-detected based on {childProfile.name}'s age</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Select Culture/Cuisine</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-3">
                  {['African', 'UK/Western', 'Asian'].map(culture => (
                    <button
                      key={culture}
                      type="button"
                      onClick={() => setNutritionData({ ...nutritionData, culture })}
                      className={`p-4 rounded-lg border-2 font-medium transition-all flex items-center gap-3 ${
                        nutritionData.culture === culture
                          ? 'border-green-600 bg-green-100 text-green-900'
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      <Globe className="h-5 w-5" />
                      {culture}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg font-bold">
              View Meal Plan 🍎
            </Button>
          </form>
        )}

        {/* Growth Tracking Form */}
        {activeSection === 'growth' && (
          <form onSubmit={handleGrowthSubmit} className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle>Current Weight</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    value={growthData.weight}
                    onChange={(e) => setGrowthData({ ...growthData, weight: e.target.value })}
                    placeholder="Enter weight"
                    className="text-lg font-semibold"
                    required
                  />
                  <span className="text-gray-600 font-medium">kg</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Current Height</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    value={growthData.height}
                    onChange={(e) => setGrowthData({ ...growthData, height: e.target.value })}
                    placeholder="Enter height"
                    className="text-lg font-semibold"
                    required
                  />
                  <span className="text-gray-600 font-medium">cm</span>
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 h-12 text-lg font-bold">
              Update Growth 📊
            </Button>
          </form>
        )}

        {/* Mental Health Form */}
        {activeSection === 'mental' && (
          <form onSubmit={handleMentalSubmit} className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Today's Mood *</CardTitle>
                <p className="text-sm text-gray-600">How is your child feeling emotionally?</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Happy', 'Calm', 'Sad', 'Fussy'].map(mood => (
                    <button
                      key={mood}
                      type="button"
                      onClick={() => setMentalData({ ...mentalData, mood })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        mentalData.mood === mood
                          ? 'border-yellow-600 bg-yellow-100 text-yellow-900'
                          : 'border-gray-300 hover:border-yellow-300'
                      }`}
                    >
                      {mood === 'Happy' && '😊 '}
                      {mood === 'Calm' && '😌 '}
                      {mood === 'Sad' && '😢 '}
                      {mood === 'Fussy' && '😤 '}
                      {mood}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Behavior Pattern *</CardTitle>
                <p className="text-sm text-gray-600">What behavior are you observing?</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Cooperative', 'Playful', 'Aggressive', 'Withdrawn', 'Defiant', 'Anxious'].map(behavior => (
                    <button
                      key={behavior}
                      type="button"
                      onClick={() => setMentalData({ ...mentalData, behavior })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        mentalData.behavior === behavior
                          ? 'border-yellow-600 bg-yellow-100 text-yellow-900'
                          : 'border-gray-300 hover:border-yellow-300'
                      }`}
                    >
                      {behavior}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Sleep Quality *</CardTitle>
                <p className="text-sm text-gray-600">How well is your child sleeping?</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {['Poor', 'Fair', 'Good'].map(quality => (
                    <button
                      key={quality}
                      type="button"
                      onClick={() => setMentalData({ ...mentalData, sleepQuality: quality })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        mentalData.sleepQuality === quality
                          ? 'border-yellow-600 bg-yellow-100 text-yellow-900'
                          : 'border-gray-300 hover:border-yellow-300'
                      }`}
                    >
                      {quality}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Appetite Level *</CardTitle>
                <p className="text-sm text-gray-600">How is your child's appetite?</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {['Poor', 'Good', 'Excellent'].map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setMentalData({ ...mentalData, appetiteLevel: level })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        mentalData.appetiteLevel === level
                          ? 'border-yellow-600 bg-yellow-100 text-yellow-900'
                          : 'border-gray-300 hover:border-yellow-300'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Energy Level *</CardTitle>
                <p className="text-sm text-gray-600">How energetic is your child?</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {['Low', 'Moderate', 'High'].map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setMentalData({ ...mentalData, energyLevel: level })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        mentalData.energyLevel === level
                          ? 'border-yellow-600 bg-yellow-100 text-yellow-900'
                          : 'border-gray-300 hover:border-yellow-300'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Social Interaction *</CardTitle>
                <p className="text-sm text-gray-600">How is your child interacting with others?</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {['Avoids Others', 'Selective', 'Plays Normally', 'Very Social'].map(interaction => (
                    <button
                      key={interaction}
                      type="button"
                      onClick={() => setMentalData({ ...mentalData, socialInteraction: interaction })}
                      className={`w-full p-3 rounded-lg border-2 font-medium text-left transition-all ${
                        mentalData.socialInteraction === interaction
                          ? 'border-yellow-600 bg-yellow-100 text-yellow-900'
                          : 'border-gray-300 hover:border-yellow-300'
                      }`}
                    >
                      {interaction}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Screen Time *</CardTitle>
                <p className="text-sm text-gray-600">Average daily screen time</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[
                    'Minimal (< 1 hour)',
                    'Moderate (1-2 hours)',
                    'High (2-3 hours)',
                    'Excessive (3+ hours)'
                  ].map(time => (
                    <button
                      key={time}
                      type="button"
                      onClick={() => setMentalData({ ...mentalData, screenTime: time })}
                      className={`w-full p-3 rounded-lg border-2 font-medium text-left transition-all ${
                        mentalData.screenTime === time
                          ? 'border-yellow-600 bg-yellow-100 text-yellow-900'
                          : 'border-gray-300 hover:border-yellow-300'
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Additional Notes (Optional)</CardTitle>
              </CardHeader>
              <CardContent>
                <textarea
                  value={mentalData.notes}
                  onChange={(e) => setMentalData({ ...mentalData, notes: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  rows={3}
                  placeholder="Any specific behaviors, triggers, or concerns?"
                />
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-yellow-600 hover:bg-yellow-700 h-12 text-lg font-bold">
              Get Mental Wellness Insights 😊
            </Button>
          </form>
        )}

        {/* Daily Health Log Form */}
        {activeSection === 'daily' && (
          <form onSubmit={handleDailyLogSubmit} className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle>Meals & Nutrition</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Excellent', 'Varied', 'Limited', 'Poor'].map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setDailyLogData({ ...dailyLogData, meals: option })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        dailyLogData.meals === option
                          ? 'border-orange-600 bg-orange-100 text-orange-900'
                          : 'border-gray-300 hover:border-orange-300'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Sleep Quality</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Good', 'Fair', 'Poor', 'Restless'].map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setDailyLogData({ ...dailyLogData, sleep: option })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        dailyLogData.sleep === option
                          ? 'border-orange-600 bg-orange-100 text-orange-900'
                          : 'border-gray-300 hover:border-orange-300'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Activity Level</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Very Active', 'Moderate', 'Low', 'Sedentary'].map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setDailyLogData({ ...dailyLogData, activity: option })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        dailyLogData.activity === option
                          ? 'border-orange-600 bg-orange-100 text-orange-900'
                          : 'border-gray-300 hover:border-orange-300'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Symptoms (if any)</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  type="text"
                  value={dailyLogData.symptoms}
                  onChange={(e) => setDailyLogData({ ...dailyLogData, symptoms: e.target.value })}
                  placeholder="e.g., Cough, Fever, None"
                />
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700 h-12 text-lg font-bold">
              Submit Daily Log 📝
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChildrensHealth;
